import { Box, Chip, Grid, Paper, Typography } from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ReactNode } from 'react';
import dayjs from 'dayjs';
import PageLoader from 'components/PageLoader';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import strings from '../../../../../common/Translation/Translate';
import axiosInstance from '../../../../../utils/axios';
import { routes } from '../../../../../utils/constants/routes';
import { URLS } from '../../../../../utils/constants/urls';
import Currency from '../../../../../utils/hooks/getCurrency';
import { getReceivableTypeLabel } from '../../../BookingDetail/BookingDetailButtons/AddReceivable/receivableUtils';

type CompanyLedgerInfoProps = {
  companyId: number;
  branchId?: number;
  level: 'company' | 'branch';
};

const formatLedgerDate = (value?: string | number | Date) => {
  if (!value) return '—';
  const date = dayjs(value);
  return date.isValid() ? date.format('DD-MM-YYYY') : '—';
};

const getReceivableTypeShortLabel = (type?: string) => {
  switch (String(type || '').toUpperCase()) {
    case 'BILL_TO_COMPANY':
      return 'BTC';
    case 'CITY_LEDGER':
      return 'City Ledger';
    default:
      return getReceivableTypeLabel(type || '');
  }
};

const getStatusLabel = (status?: string) => {
  switch (String(status || '').toUpperCase()) {
    case 'OPEN':
      return strings.receivable_status_open;
    case 'PARTIALLY_SETTLED':
      return strings.receivable_status_partiallySettled;
    case 'SETTLED':
      return strings.receivable_status_settled;
    case 'CANCELLED':
      return strings.receivable_status_cancelled;
    default:
      return status || '—';
  }
};

const SETTLED_STATUS_CHIP_SX = { backgroundColor: '#E6F4EA', color: '#137333', fontWeight: 600, fontSize: 11 };
const EMBER_STATUS_CHIP_SX = { backgroundColor: '#FEF7E0', color: '#B06000', fontWeight: 600, fontSize: 11 };

const cellTextSx = {
  fontSize: '0.8125rem',
  lineHeight: 1.35,
} as const;

const getDueDateInfo = (dueDate?: string | number | Date, status?: string) => {
  const normalizedStatus = String(status || '').toUpperCase();
  const due = dayjs(dueDate).startOf('day');
  const today = dayjs().startOf('day');
  const isSettledOrCancelled = normalizedStatus === 'SETTLED' || normalizedStatus === 'CANCELLED';
  const isOverdue = !isSettledOrCancelled && due.isValid() && due.isBefore(today);
  const daysOverdue = isOverdue ? today.diff(due, 'day') : 0;

  return { isOverdue, daysOverdue };
};

const StatusChip = ({ status }: { status?: string }) => {
  const isSettled = String(status || '').toUpperCase() === 'SETTLED';
  return (
    <Chip
      label={getStatusLabel(status)}
      size="small"
      sx={{
        ...(isSettled ? SETTLED_STATUS_CHIP_SX : EMBER_STATUS_CHIP_SX),
        height: 22,
      }}
    />
  );
};

const DueDateCell = ({ dueDate, status }: { dueDate?: string | number | Date; status?: string }) => {
  if (!dueDate) {
    return (
      <Typography variant="body2" sx={cellTextSx}>
        —
      </Typography>
    );
  }

  const { isOverdue, daysOverdue } = getDueDateInfo(dueDate, status);

  return (
    <Box sx={{ py: 0.75 }}>
      <Typography
        variant="body2"
        sx={{
          ...cellTextSx,
          fontWeight: isOverdue ? 700 : 500,
          color: isOverdue ? '#D93025' : 'text.primary',
        }}>
        {formatLedgerDate(dueDate)}
      </Typography>
      {isOverdue && (
        <Typography variant="caption" sx={{ color: '#D93025', fontSize: 11, fontWeight: 600, display: 'block' }}>
          {daysOverdue === 1
            ? strings.company_master_ledger_day_overdue
            : strings.company_master_ledger_days_overdue.replace('{0}', String(daysOverdue))}
        </Typography>
      )}
    </Box>
  );
};

const LedgerKpiCard = ({
  label,
  value,
  color,
  bgColor,
  icon,
  valueColor,
}: {
  label: string;
  value: string;
  color: string;
  bgColor: string;
  icon: ReactNode;
  valueColor?: string;
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      border: '1px solid',
      borderColor: color,
      borderRadius: 2,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
    }}>
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: bgColor,
        color,
        flexShrink: 0,
      }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: valueColor || color, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const CompanyLedgerInfo = ({ companyId, branchId, level }: CompanyLedgerInfoProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const url =
        level === 'branch' && branchId
          ? URLS.getBranchLedger(companyId, branchId)
          : URLS.getCompanyLedger(companyId);

      const { data } = await axiosInstance.get(url);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, level]);

  useEffect(() => {
    if (level === 'branch' ? Boolean(branchId) : Boolean(companyId)) {
      void fetchData();
    }
  }, [companyId, branchId, level, fetchData]);

  const activeRows = useMemo(
    () => rows.filter(row => String(row?.status || '').toUpperCase() !== 'CANCELLED'),
    [rows],
  );

  const kpis = useMemo(() => {
    const totalAmount = activeRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    const receivedTotal = activeRows.reduce((sum, row) => sum + (Number(row.receivedAmount) || 0), 0);
    const totalDue = activeRows.reduce((sum, row) => sum + (Number(row.pendingAmount) || 0), 0);
    return { totalAmount, receivedTotal, totalDue };
  }, [activeRows]);

  const columns = useMemo((): GridColDef[] => {
    const partyColumn: GridColDef =
      level === 'branch'
        ? {
            field: 'customerAndType',
            headerName: strings.company_master_ledger_column_customer,
            flex: 1.2,
            minWidth: 170,
            sortable: false,
            renderCell: ({ row }: GridRenderCellParams) => (
              <Box sx={{ py: 0.75 }}>
                <Typography variant="body2" sx={{ ...cellTextSx, fontWeight: 600 }}>
                  {row.customerName || row.contactName || '—'}
                </Typography>
                <Typography variant="body2" sx={{ ...cellTextSx, color: 'text.secondary' }}>
                  {strings.receivable_column_type}: {getReceivableTypeShortLabel(row.type)}
                </Typography>
              </Box>
            ),
          }
        : {
            field: 'branchAndType',
            headerName: strings.receivable_field_branch,
            flex: 1.2,
            minWidth: 170,
            sortable: false,
            renderCell: ({ row }: GridRenderCellParams) => (
              <Box sx={{ py: 0.75 }}>
                <Typography variant="body2" sx={{ ...cellTextSx, fontWeight: 600 }}>
                  {row.salesBranchName || '—'}
                </Typography>
                <Typography variant="body2" sx={{ ...cellTextSx, color: 'text.secondary' }}>
                  {strings.receivable_column_type}: {getReceivableTypeShortLabel(row.type)}
                </Typography>
              </Box>
            ),
          };

    return [
      {
        field: 'eventDetail',
        headerName: strings.company_master_ledger_column_event,
        flex: 1.4,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams) => (
          <Box sx={{ py: 0.75 }}>
            <Typography variant="body2" sx={{ ...cellTextSx, fontWeight: 600 }}>
              {formatLedgerDate(row.eventDate || row.createdAt)}
            </Typography>
            <Typography variant="body2" sx={cellTextSx}>
              {row.eventTypeName || '—'}
            </Typography>
            <Typography variant="body2" sx={{ ...cellTextSx, color: 'text.secondary' }}>
              {row.venueName || '—'}
            </Typography>
          </Box>
        ),
      },
      partyColumn,
      {
        field: 'dueDate',
        headerName: strings.company_master_ledger_column_due_date,
        flex: 0.9,
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams) => (
          <DueDateCell dueDate={row.dueDate} status={row.status} />
        ),
      },
      {
        field: 'formattedAmount',
        headerName: strings.receivable_column_amount,
        flex: 0.9,
        minWidth: 110,
        align: 'right',
        headerAlign: 'right',
        valueGetter: ({ row }) => row.formattedAmount || row.amount || '—',
      },
      {
        field: 'formattedReceivedAmount',
        headerName: strings.receivable_column_received,
        flex: 0.9,
        minWidth: 110,
        align: 'right',
        headerAlign: 'right',
        valueGetter: ({ row }) => row.formattedReceivedAmount || row.receivedAmount || '—',
      },
      {
        field: 'formattedPendingAmount',
        headerName: strings.receivable_column_pending,
        flex: 0.9,
        minWidth: 110,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }: GridRenderCellParams) => {
          const pendingNum = Number(row.pendingAmount) || 0;
          const pendingColor = pendingNum > 0 ? '#D93025' : 'text.secondary';
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: pendingNum > 0 ? 700 : 500,
                color: pendingColor,
                fontVariantNumeric: 'tabular-nums',
              }}>
              {row.formattedPendingAmount || row.pendingAmount || '—'}
            </Typography>
          );
        },
      },
      {
        field: 'status',
        headerName: strings.receivable_column_status,
        flex: 0.9,
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams) => <StatusChip status={row.status} />,
      },
    ];
  }, [level]);

  const handleRowClick = (params: any) => {
    const lineItemId = params?.row?.bookingLineItemId;
    if (lineItemId) {
      navigate(`${routes.bookings}/${lineItemId}`);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
        {strings.company_master_ledger_empty}
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <LedgerKpiCard
            label={strings.company_master_ledger_kpi_total_amount}
            value={Currency(kpis.totalAmount)}
            color="#9AA0A6"
            bgColor="rgba(95, 99, 104, 0.08)"
            valueColor="text.primary"
            icon={<AccountBalanceWalletOutlinedIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <LedgerKpiCard
            label={strings.company_master_ledger_kpi_received_total}
            value={Currency(kpis.receivedTotal)}
            color="#188038"
            bgColor="rgba(24, 128, 56, 0.1)"
            icon={<CheckCircleOutlineIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <LedgerKpiCard
            label={strings.company_master_ledger_kpi_total_due}
            value={Currency(kpis.totalDue)}
            color="#E37400"
            bgColor="rgba(227, 116, 0, 0.1)"
            icon={<ErrorOutlineIcon />}
          />
        </Grid>
      </Grid>

      <Box sx={{ height: 420, width: '100%' }}>
        <DataGrid
          sx={{
            '.MuiDataGrid-row': { cursor: 'pointer' },
            '.MuiDataGrid-cell': { alignItems: 'flex-start', py: 0.5 },
          }}
          rowHeight={84}
          rows={rows.map((row, index) => ({
            id: row.id ?? `${level}-${index}`,
            ...row,
          }))}
          columns={columns}
          onRowClick={handleRowClick}
          disableSelectionOnClick
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Box>
    </Box>
  );
};

export default CompanyLedgerInfo;
